<template>
  <v-card flat tile>
    <v-toolbar color="secondary" dark>
      <v-toolbar-title>{{ headerTitle }}</v-toolbar-title>
      <v-spacer />
      <v-menu bottom origin="center center" transition="scale-transition">
        <template v-slot:activator="{ on, attrs }">
          <v-btn dark v-bind="attrs" v-on="on">
            <v-icon>mdi-alpha-a-circle</v-icon>
            Actions
          </v-btn>
        </template>
        <v-list>
          <v-list-item @click="openTemplate"><v-list-item-title>Open Template</v-list-item-title></v-list-item>
          <v-list-item @click="htmlPreview"><v-list-item-title>Browser Preview</v-list-item-title></v-list-item>
          <!-- <v-list-item @click="sendEmailPreview"><v-list-item-title>Email Preview</v-list-item-title></v-list-item> -->
          <v-list-item @click="$router.go(-1)"><v-list-item-title>Exit Without Save</v-list-item-title></v-list-item>
        </v-list>
      </v-menu>
      <input type="file" ref="templateUploadInput" hidden="true" @change="loadTemplate" />

      <v-btn :depressed="true" color="secondary" @click.prevent="validateAndSubmit">
        <v-icon left dark>mdi-content-save</v-icon>
        Save
      </v-btn>
    </v-toolbar>
    <!-- INPUT -->
    <v-form ref="snippetForm">
      <v-row>
        <v-col cols="12">
          <v-row align="center" justify="center">
            <v-col md="4">
              <v-text-field v-model="snippet.name" required :rules="vruleFieldRequired" placeholder="Snippet Name"
                hint="Snippet Name" persistent-hint>
              </v-text-field>
            </v-col>
            <v-col md="2">
              <v-select v-model="snippet.type" :items="snippetTypes" item-text="title" item-value="name"
                hint="Snippet Type" persistent-hint required :rules="vruleFieldRequired"></v-select>
            </v-col>
            <v-col md="2">
              <v-checkbox v-model="snippet.is_default" label="Is Default" persistent-hint
                hint="for type (header,footer)"></v-checkbox>
            </v-col>
          </v-row>
        </v-col>
      </v-row>

      <!-- The Editor -->
      <v-row no-gutters>
        <v-col md="10">
          <codemirror v-model="snippet.template_html" :options="editorOptions">
          </codemirror>
        </v-col>
        <v-col md="2">
          <v-toolbar color="secondary" align="center" justify="center">
            <v-spacer />
            <v-btn-toggle v-model="selectedSidebarViewIndex" mandatory dark>
              <v-btn v-for="sidebarItem in sidebarItems" v-bind:key="sidebarItem.name"
                @click="selectSidebarView(sidebarItem.component)" color="secondary" plain :depressed="true">
                <v-icon>{{ sidebarItem.icon }}</v-icon>
              </v-btn>
            </v-btn-toggle>
            <v-spacer />
          </v-toolbar>
          <component v-bind:is="selectedSidebarView" v-bind:html.sync="snippetHtml" help-url="/help/CustomEditor.md"
            :template-key="templateKey" titlePadding="pl-2" listPadding="pl-4" searchPadding="pl-4"></component>
        </v-col>
      </v-row>
    </v-form>
  </v-card>
</template>

<script>
import validationMixin from '@/validators/validations_mixin'
import { R4UtilsApi, HtmlSnippetApi } from '@/services'
import MergeTagSidebar from '../MergeTagSidebar'
import SpecialLinkSidebar from '../SpecialLinkSidebar'
import HelpSidebar from '../HelpSidebar'
import ImageSidebar from '../ImageSidebar'
import codemirror from '@/components/shared/codemirror'
import { mapState } from 'vuex'
import { path, propOr } from 'ramda'

const { v4: uuid } = require('uuid')

const CodeMirror = require('codemirror/lib/codemirror.js')

export default {
  name: 'builder-snippet-editor',
  mixins: [validationMixin],
  components: {
    [MergeTagSidebar.name]: MergeTagSidebar,
    [SpecialLinkSidebar.name]: SpecialLinkSidebar,
    [ImageSidebar.name]: ImageSidebar,
    [HelpSidebar.name]: HelpSidebar,
    [codemirror.name]: codemirror
  },
  props: {
    headerTitle: {
      required: false,
      type: String,
      default: 'Snippet Editor'
    },
    snippetId: {
      required: false,
      type: [String, Number]
    }
  },
  data: function () {
    return {
      snippetTypes: [],
      snippet: {},
      selectedSidebarViewIndex: 0,
      selectedSidebarView: MergeTagSidebar.name,
      editorOptions: {
        mode: {
          name: 'htmlmixed',
          scriptTypes: [{ matches: /\/x-handlebars-template|\/x-mustache/i, mode: null }]
        },
        lineNumbers: true,
        lineWrapping: false,
        theme: 'solarized light',
        extraKeys: { 'Ctrl-Space': 'autocomplete' },
        hintOptions: { hint: this.mergeTagHints },
        viewportMargin: Infinity
      },
      sidebarItems: [
        { icon: 'mdi-tag-multiple', text: 'Merge Tags', component: MergeTagSidebar.name },
        { icon: 'mdi-link-variant', text: 'Special Links', component: SpecialLinkSidebar.name },
        { icon: 'mdi-image', text: 'Images', component: ImageSidebar.name },
        { icon: 'mdi-help', text: 'Help', component: HelpSidebar.name }
      ]
      // showSendPreviewDialog: false,
      // isSendingEmailPreview: false
    }
  },
  computed: {
    ...mapState('DealerStore', ['dealer']),
    ...mapState('EmailTemplateStore', ['mergeTags', 'specialLinks']),
    snippetHtml: {
      get: function () {
        return propOr('', 'template_html', this.snippet)
      },
      set: function (val) {
        this.$set(this.snippet, 'template_html', val)
      }
    },
    snippetJson: {
      get: function () {
        return propOr({}, 'template_json', this.snippet)
      },
      set: function (val) {
        this.$set(this.snippet, 'template_json', val)
      }
    },
    templateKey() {
      return this.snippetJson.template_key
    },
    allTags() {
      return this.mergeTags.map(mt => mt.value).concat(
        ...this.specialLinks.map(sl => sl.link)
      )
    }

  },
  methods: {
    mergeTagHints(cm, option) {
      const that = this
      return new Promise(function (resolve, reject) {
        setTimeout(function () {
          let cursor = cm.getCursor()
          let line = cm.getLine(cursor.line)
          let start = cursor.ch
          let end = cursor.ch
          while (start && /\w/.test(line.charAt(start - 1))) { --start }
          while (end < line.length && /\w/.test(line.charAt(end))) { ++end }

          const hits = []
          let word = line.slice(start, end).toLowerCase()
          for (let i = 0; i < that.allTags.length; i++) {
            if (that.allTags[i].includes(word)) {
              hits.push(that.allTags[i])
            }
          }
          return resolve({
            list: hits,
            from: CodeMirror.Pos(cursor.line, start),
            to: CodeMirror.Pos(cursor.line, end)
          })
        })
      })
    },
    loadSnippetTypes() {
      HtmlSnippetApi.getTypes()
        .then((types) => {
          this.snippetTypes = types.data
        })
        .catch(error => {
          this.$logger.error('Error loading snippet types', error.message)
          this.$flash({
            showClose: true,
            message: 'Error loading snippet types. Reload and try again. ' + error.message,
            type: 'error',
            duration: 0
          })
        })
    },
    configureTemplateKey(snippetData) {
      // set the templateKey
      const tkey = path(['template_json', 'template_key'], snippetData)
      if (!tkey) {
        snippetData.template_json.template_key = uuid()
      }
      return snippetData
    },
    loadSnippet() {
      if (this.snippetId) {
        HtmlSnippetApi.get(this.snippetId)
          .then((resp) => {
            // set the data & configure key
            this.snippet = this.configureTemplateKey(resp.data)
          })
          .catch(error => {
            this.$logger.error(error.message)
            this.$flash({
              showClose: true,
              message: 'Error loading html snippet. ' + error.message,
              type: 'error',
              duration: 0
            })
          })
      } else {
        this.snippet = this.configureTemplateKey({
          template_html: '',
          template_json: {}

        })
      }
    },
    selectSidebarView: function (componentName) {
      this.selectedSidebarView = componentName
    },
    validateAndSubmit: function () {
      if (this.$refs.snippetForm.validate()) {
        this.save()
      }
    },
    save: function () {
      return HtmlSnippetApi.save(this.snippet)
        .then(response => {
          this.$emit('on-save', response.data)
        })
        .catch(error => {
          this.$flash({
            showClose: true,
            message: 'Error saving html snippet. ' + error.message,
            duration: 0,
            type: 'error'
          })
        })
    },
    openTemplate() {
      this.$refs['templateUploadInput'].click()
    },
    loadTemplate() {
      const file = this.$refs['templateUploadInput'].files[0]
      const reader = new FileReader()
      reader.onload = function (fileLoadedEvent) {
        this.snippetHtml = fileLoadedEvent.target.result
        this.$refs['templateUploadInput'].value = '' // reset input to fix bug where @change event does not work 2nd time
      }.bind(this)
      reader.readAsText(file)
    },
    sendEmailPreview: function () {
      // this.resetModalError()
      this.showSendPreviewDialog = true
      this.snippetPreviewHtml = this.snippetHtml
    },
    htmlPreview: function () {
      HtmlSnippetApi.liveview(this.dealer.id, this.snippetHtml)
        .then((htmlTemplatePreview) => {
          R4UtilsApi.openHtmlInNewTab(htmlTemplatePreview.data.html)
        })
        .catch(error => {
          this.$flash({
            showClose: true,
            message: 'Error previewing snippet. ' + error.message,
            type: 'error',
            duration: 0
          })
        })
    }
  },
  mounted() {
    this.$store.dispatch('EmailTemplateStore/getMergeTags')
    this.$store.dispatch('EmailTemplateStore/getSpecialLinks')
    this.loadSnippetTypes()
    this.loadSnippet()
  }
}
</script>

<style></style>
